"use client";

// A link that prefetches when someone shows they mean to follow it, not when it
// scrolls into view.
//
// ⚠️ WHY NOT THE DEFAULT. A default <Link> prefetches its page the moment it is
// in the viewport, so the sidebar alone asked for ~48 prefetches on every page
// load (three per entry: the page's `/_tree`, its metadata, and a `/_tree` for
// the same page without `?as=`), each through the proxy. Every page here is
// dynamic and none has a loading.tsx, so a prefetch buys the route's tree, not
// its data: a click without one still costs the one fetch it always did.
//
// ⚠️ WHY THE HANDLERS ARE OURS. `prefetch={false}` turns off Next's hover
// prefetch too (link.js returns early from its own onMouseEnter and
// onTouchStart when prefetch is off), so the intent has to be added back here:
// pointer over it, keyboard focus on it, or a finger down on it, each asking
// `router.prefetch(href)` once. The router's segment cache dedupes a second ask
// for a page it holds, and `router.prefetch` is a no-op under `next dev`, as
// Next's own prefetches are.
//
// ⚠️ AND IT LOADS THE PAGE'S STORE GROUPS ON THE SAME INTENT
// (lib/store-intent.ts). The route's tree is the small half of a click into the
// script pages; the store groups they wait on are the big one (the Videos
// page's ~800 KB), and they load through the store's poll, not the router, so
// `router.prefetch` never asked for them.
//
// Used for the chrome on every page (the sidebar, the topbar's crumbs) and for a
// button to another page (a brand's Metrics and New affiliate campaign, a
// campaign step's Back and Next). A link that is one of many (a table's rows, a
// pager) is `RowLink`, which never prefetches: sweeping the pointer down a
// table is not intent to open every row.

import Link from "next/link";
import { useAppRouter } from "@/lib/app-router";
import { loadGroupsOnIntent } from "@/lib/store-intent";
import type { ComponentProps, FocusEvent, MouseEvent, TouchEvent } from "react";

type Props = Omit<ComponentProps<typeof Link>, "href" | "prefetch"> & {
  /** A string, because `router.prefetch` takes one. */
  href: string;
};

export function IntentLink({ href, onMouseEnter, onFocus, onTouchStart, ...rest }: Props) {
  const router = useAppRouter();
  const intent = () => {
    router.prefetch(href);
    loadGroupsOnIntent(href);
  };
  return (
    <Link
      {...rest}
      href={href}
      prefetch={false}
      onMouseEnter={(e: MouseEvent<HTMLAnchorElement>) => {
        onMouseEnter?.(e);
        intent();
      }}
      onFocus={(e: FocusEvent<HTMLAnchorElement>) => {
        onFocus?.(e);
        intent();
      }}
      onTouchStart={(e: TouchEvent<HTMLAnchorElement>) => {
        onTouchStart?.(e);
        intent();
      }}
    />
  );
}
